import {ActionPerformed, PushNotifications, PushNotificationSchema} from '@capacitor/push-notifications'
import useEmitter from "@/helper/emitter";
import {Capacitor} from "@capacitor/core";
import {RouteParamsRaw} from 'vue-router';
import {consoleErrorApp, consoleLogApp} from "@/helper/console";
import {RegistrationError, Token} from "@capacitor/push-notifications/dist/esm/definitions";
import {timerPromise} from "@/helper/timerPromise";

export interface NotificationType {
  title: string;
  body: string;
  view: string;
  props: RouteParamsRaw;
}

const emitter = useEmitter()

const debug = () => import.meta.env.VITE_DEBUG_PUSH_NOTIFICATIONS === 'true';

let initialized = false;
const init = () => {
    if (isAvailable() && !debug()) {
        PushNotifications.addListener('pushNotificationActionPerformed', onPushNotificationActionPerformed);
        PushNotifications.addListener('pushNotificationReceived', onNotificationReceived);
    }
}

const isAvailable = () => {
    return debug() || Capacitor.isPluginAvailable('PushNotifications');
}

const _checkPermissions = async () => debug() ? {receive:'prompt'} : await PushNotifications.checkPermissions()
const _requestPermissions = async () => debug() ? {receive:'granted'} : await PushNotifications.requestPermissions()
const _addListenerRegistration = async (listenerFunc: (token: Token) => void) => debug() ? timerPromise(100).then(() => listenerFunc({value:'PushNotificationDebugToken'})) : await PushNotifications.addListener('registration',listenerFunc)
const _addListenerRegistrationError = async (listenerFunc: (error: RegistrationError) => void) => debug() ? /*timerPromise(100).then(() => listenerFunc({error:'debugError'}))*/ null : await PushNotifications.addListener('registrationError',listenerFunc)
const _register = () => debug() ? null : PushNotifications.register()

export const registerToPushNotifications = async (maxRetries = 3, delay = 2000) : Promise<boolean> => {
    if (!isAvailable()) {
        throw new Error('Push notifications not available');
    }

    let permissionStatus = await _checkPermissions();

    if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
        permissionStatus = await _requestPermissions();
    }

    if (permissionStatus.receive !== 'granted') {
        throw new Error('Push notification permission not granted ('+permissionStatus.receive+')');
    }

    consoleLogApp("Start registering for push notifications");

    const registerWithRetry = async () => {
        let isDone = false;

        const promise =  new Promise((resolve, reject):void => {
            const timeout = setTimeout(() => {
                if(isDone)  return;
                isDone = true;
                reject(new Error('Registration event timeout'));
            }, delay);

            _addListenerRegistration(async (token) => {
                if(isDone)  return;
                clearTimeout(timeout);
                isDone = true;
                consoleLogApp('APNs device token:', token.value);
                resolve(true);
            });

            _addListenerRegistrationError((error) => {
                if(isDone)  return;
                clearTimeout(timeout);
                isDone = true;
                consoleErrorApp('Registration error:', error);
                reject(error);
            });
            _register()
        });
        consoleLogApp("Push notification registration requested.");
        await promise;
    };

    let attempt = 0;
    let success = false;
    while (attempt <= maxRetries && !success) {
        attempt ++
        try {
            consoleLogApp(`Attempt ${attempt} to register for push notifications...`);
            await registerWithRetry()
            success = true;
            consoleLogApp(`Registration attempt ${attempt} succeeded.`);

        } catch (error){
            success = false;
            consoleLogApp(`Registration attempt ${attempt} failed. Retrying in ${delay}ms...`);
        }
    }
    if(!success) {
        consoleErrorApp('Max registration attempts reached. Registration failed.');
    }

    return success;
};


const onPushNotificationActionPerformed = (notificationAction: ActionPerformed) => {
    const viewName = JSON.parse(notificationAction.notification.data.view)
    const viewProps = JSON.parse(notificationAction.notification.data.props)
    const title: string = notificationAction.notification.title ? notificationAction.notification.title : ""
    const body: string = notificationAction.notification.body ? notificationAction.notification.body : ""

    emitter.emit('pushNotification:actionPerformed', {
        title: title,
        body: body,
        view: viewName,
        props: viewProps
    })
}


const onNotificationReceived = (notification: PushNotificationSchema) => {
    const viewName = JSON.parse(notification.data.view)
    const viewProps = JSON.parse(notification.data.props)
    const title: string = notification.title ? notification.title : ""
    const body: string = notification.body ? notification.body : ""

    emitter.emit('pushNotification:received', {
        title: title,
        body: body,
        view: viewName,
        props: viewProps
    })
}

export default function usePushNotificationHelper() {
    if (!initialized) {
        init()
        initialized = true
    }

    return {
        isAvailable,
        registerToPushNotifications,
        onPushNotificationActionPerformed: (f:(notification:NotificationType)=>void) => emitter.on('pushNotification:actionPerformed', f),
        onNotificationReceived: (f:(notification:NotificationType)=>void) => emitter.on('pushNotification:received', f),
    };
}
